import React, { useRef, useState } from 'react';
import { Button, Grid } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable } from '../../../../../Components';
import { DateField } from '../../../../../Components/Field';
import { TypedFormFieldValue } from '../../../../../Components/Form/Form.types';
import { RichTextEditor } from '../../../../../Components/RichTextEditor/RichTextEditor';
import { OnboardingDraftSelect, OnboardingDraftText, emptyRequirement, useOnboardingDraft, validRequirement } from './OnboardingDraft';
import { useStyles } from './Onboarding.styles';

const toDateOnly = (value?: TypedFormFieldValue<Date>) =>
  value && !Number.isNaN(value.value.getTime())
    ? `${value.value.getFullYear()}-${String(value.value.getMonth() + 1).padStart(2, '0')}-${String(value.value.getDate()).padStart(2, '0')}`
    : undefined;
const fromDateOnly = (value?: string) => {
  if (!value) return undefined;
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : { value: date };
};
const datePickerPopoverProps = {
  transitionDuration: 0,
  PaperProps: { style: { visibility: 'hidden' as const } },
  TransitionProps: {
    onEntering: (element: HTMLElement) => {
      window.requestAnimationFrame(() => { element.style.visibility = 'visible'; });
    },
  },
};

export const OnboardingRequirements = () => {
  const { draft, update, account, areas } = useOnboardingDraft();
  const { formatMessage: t } = useIntl();
  const classes = useStyles();
  const [formOpen, setFormOpen] = useState(false);
  const [description, setDescription] = useState('');
  const descriptionRef = useRef('');
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const item = draft.requirement;
  const patch = (change: Partial<typeof item>) => update(current => ({ ...current, requirement: { ...current.requirement, ...change } }));
  const requirementsById = new Map<number, typeof account.otherRequirements>();
  (account.otherRequirements || []).forEach(requirement => {
    const rows = requirementsById.get(requirement.id) || [];
    rows.push(requirement);
    requirementsById.set(requirement.id, rows);
  });
  const draftRequirementsById = new Map(draft.requirements.filter(requirement => requirement.id).map(requirement => [requirement.id!, requirement]));
  const existingRequirements = Array.from(requirementsById.values())
    .map(rows => rows[0])
    .filter(requirement => !(draft.requirementIdsToDelete || []).includes(requirement.id));
  const unitNamesById = new Map(draft.units.map(unit => [unit.id, unit.name]));
  const rows = [
    ...existingRequirements.map(requirement => {
      const edited = draftRequirementsById.get(requirement.id);
      const units = edited
        ? edited.unitIds.map(id => unitNamesById.get(id)).filter(Boolean).join(', ')
        : Array.from(new Set((requirementsById.get(requirement.id) || []).map(row => row.companyUnitName))).join(', ');
      return { id: requirement.id, name: edited?.name || requirement.name, units };
    }),
    ...draft.requirements.filter(requirement => !requirement.id || requirement.id < 0).map(requirement => ({
      id: requirement.id || -1,
      name: requirement.name,
      units: requirement.unitIds.map(id => unitNamesById.get(id)).filter(Boolean).join(', '),
    })),
  ];
  const edit = (id: number) => {
    // Table action callbacks may retain an older render; always open the latest saved draft.
    const draftRequirement = draftRef.current.requirements.find(requirement => requirement.id === id);
    if (draftRequirement) {
      descriptionRef.current = draftRequirement.description;
      setDescription(draftRequirement.description);
      update(current => ({ ...current, requirement: { ...draftRequirement, unitIds: [...draftRequirement.unitIds] } }));
    } else {
      const requirement = existingRequirements.find(item => item.id === id);
      if (!requirement) return;
      descriptionRef.current = requirement.description;
      setDescription(requirement.description);
      update(current => ({ ...current, requirement: {
        id: requirement.id, name: requirement.name, area: requirement.area,
        effectiveFrom: requirement.effectiveFrom.slice(0, 10), effectiveTo: requirement.effectiveTo?.slice(0, 10),
        description: requirement.description, unitIds: Array.from(new Set((requirementsById.get(id) || []).map(row => row.companyUnitId))),
      } }));
    }
    setFormOpen(true);
  };
  const save = () => {
    update(current => {
      const edited = { ...current.requirement, description: descriptionRef.current };
      if (edited.id) {
        const exists = current.requirements.some(requirement => requirement.id === edited.id);
        return { ...current, requirements: exists
          ? current.requirements.map(requirement => requirement.id === edited.id ? edited : requirement)
          : [...current.requirements, edited], requirement: emptyRequirement() };
      }
      const id = Math.min(0, ...current.requirements.map(requirement => requirement.id || 0)) - 1;
      return { ...current, requirements: [...current.requirements, { ...edited, id }], requirement: emptyRequirement() };
    });
    setFormOpen(false);
  };
  const cancel = () => {
    descriptionRef.current = '';
    setDescription('');
    update(current => ({ ...current, requirement: emptyRequirement() }));
    setFormOpen(false);
  };
  const remove = (id: number) => update(current => ({
    ...current,
    requirements: current.requirements.filter(requirement => requirement.id !== id),
    requirementIdsToDelete: id > 0 ? Array.from(new Set([...(current.requirementIdsToDelete || []), id])) : current.requirementIdsToDelete,
    requirement: current.requirement.id === id ? emptyRequirement() : current.requirement,
  }));
  const form = <Grid key={item.id || 'new-requirement'} container spacing={3} className={classes.adminFields}>
      <Grid item xs={12} sm={6}><OnboardingDraftText label={t({ id: 'form.name' })} value={item.name} onChange={name => patch({ name })} /></Grid>
      <Grid item xs={12} sm={6}><OnboardingDraftSelect name="requirement-area" label={t({ id: 'utils.filters.area' })} ids={item.area ? [item.area] : []} options={areas} multi={false} required onChange={ids => patch({ area: ids[0] || 0 })} /></Grid>
      <Grid item xs={12} sm={6}><DateField name="effectiveFrom" label={t({ id: 'law-portal.manage.requirements.effectiveFrom' })} required initialState={fromDateOnly(item.effectiveFrom)} errorTexts={[]} PopoverProps={datePickerPopoverProps} onChange={(value?: TypedFormFieldValue<Date>) => patch({ effectiveFrom: toDateOnly(value) || '' })} /></Grid>
      <Grid item xs={12} sm={6}><DateField name="effectiveTo" label={t({ id: 'law-portal.manage.requirements.effectiveTo' })} initialState={fromDateOnly(item.effectiveTo)} errorTexts={[]} PopoverProps={datePickerPopoverProps} onChange={(value?: TypedFormFieldValue<Date>) => patch({ effectiveTo: toDateOnly(value) })} /></Grid>
      <Grid item xs={12} sm={6}><OnboardingDraftSelect name="requirement-units" label={t({ id: 'form.selectCompanyUnits' })} ids={item.unitIds} options={draft.units} required onChange={unitIds => patch({ unitIds })} /></Grid>
      <Grid item xs={12}><RichTextEditor value={description} onChange={value => { descriptionRef.current = value; setDescription(value); patch({ description: value }); }} overrideConfig={{ height: 180 }} /></Grid>
      <Grid item xs={12} className={classes.formActions}>
        <Button variant="outlined" color="primary" disabled={!validRequirement({ ...item, description })} onClick={save}>{t({ id: item.id ? 'form.save' : 'form.add' })}</Button>
        <Button style={{ marginLeft: 8 }} onClick={cancel}>{t({ id: 'form.cancel' })}</Button>
      </Grid>
    </Grid>;
  const table = <MTable data={rows} columns={[{ title: t({ id: 'onboarding.requirement-name' }), field: 'name' }, { title: t({ id: 'law-portal.overview-notes.form.company-units.label' }), field: 'units' }]} actions={[
    row => ({ icon: 'edit', tooltip: t({ id: 'component.mtable.editTooltip' }), onClick: () => edit(row.id) }),
    row => ({ icon: 'delete', tooltip: t({ id: 'component.mtable.deleteTooltip' }), onClick: () => remove(row.id) }),
  ]} />;
  return <>
    <div className={classes.listActions}>
      {!formOpen && <Button color="primary" variant="outlined" onClick={() => { descriptionRef.current = ''; setDescription(''); setFormOpen(true); }}>{t({ id: 'onboarding.add-requirement' })}</Button>}
    </div>
    {formOpen && form}
    {table}
  </>;
};
